// Map sprite images drawn on canvas at 2x so they stay crisp on retina.
// Shapes follow FloodIcons.swift: flash flood kinds are triangles with a
// pulsing halo, the others are circles with an exclamation mark.
import type { Map as MaplibreMap, StyleImageInterface } from 'maplibre-gl';
import { ALERT_KINDS, ALERT_STYLE, type AlertKind } from '../config/alerts';
import { FLOOD_CATEGORY_INFO, type FloodCategory } from '../config/gauges';
import { PALETTE } from '../config/constants';

export const ICON_SIZE = 36;
const RATIO = 2;

export function alertIconId(kind: AlertKind): string {
  return `alert-${kind}`;
}

export function haloIconId(kind: AlertKind): string {
  return `halo-${kind}`;
}

export const SEARCH_PIN_ICON = 'search-pin';
export const USER_LOCATION_ICON = 'user-location';

function canvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = size * RATIO;
  c.height = size * RATIO;
  const ctx = c.getContext('2d')!;
  ctx.scale(RATIO, RATIO);
  return [c, ctx];
}

function imageData(c: HTMLCanvasElement): ImageData {
  return c.getContext('2d')!.getImageData(0, 0, c.width, c.height);
}

function drawExclamation(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color = '#000') {
  ctx.fillStyle = color;
  ctx.font = `900 ${size}px -apple-system, "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('!', cx, cy);
}

function trianglePath(ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number) {
  const h = size * 0.9;
  ctx.beginPath();
  ctx.moveTo(cx, cy - h / 2);
  ctx.lineTo(cx + size / 2, cy + h / 2);
  ctx.lineTo(cx - size / 2, cy + h / 2);
  ctx.closePath();
}

/** Static alert icon: 36 pt circle with black stroke, or a black-outlined triangle. */
export function drawAlertIcon(kind: AlertKind): ImageData {
  const style = ALERT_STYLE[kind];
  const pad = 3;
  const size = ICON_SIZE + pad * 2;
  const [c, ctx] = canvas(size);
  const cx = size / 2;
  const cy = size / 2;
  ctx.shadowColor = 'rgba(0,0,0,0.3)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetY = 1;
  if (style.shape === 'triangle') {
    trianglePath(ctx, cx, cy + 1, 34);
    ctx.fillStyle = '#000';
    ctx.fill();
    ctx.shadowColor = 'transparent';
    trianglePath(ctx, cx, cy + 2.5, 26);
    ctx.fillStyle = style.color;
    ctx.fill();
    drawExclamation(ctx, cx, cy + 6, 15);
  } else {
    ctx.beginPath();
    ctx.arc(cx, cy, ICON_SIZE / 2 - 1, 0, Math.PI * 2);
    ctx.fillStyle = style.color;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.lineWidth = 2;
    ctx.strokeStyle = 'rgba(0,0,0,0.85)';
    ctx.stroke();
    drawExclamation(ctx, cx, cy + 1, 22);
  }
  return imageData(c);
}

/**
 * Pulsing halo (FlashFloodPolygonIcon): a ring that scales from 1x to 2.2x and
 * fades from 0.85 to 0 over 1.2 s, repeating. Implemented as an animated style
 * image so the symbol layer repaints each frame.
 */
export function pulsingHalo(map: MaplibreMap, color: string): StyleImageInterface {
  const maxScale = 2.2;
  const size = Math.ceil(ICON_SIZE * maxScale) + 8;
  const px = size * RATIO;
  const c = document.createElement('canvas');
  c.width = px;
  c.height = px;
  const ctx = c.getContext('2d')!;
  const duration = 1200;
  return {
    width: px,
    height: px,
    data: new Uint8ClampedArray(px * px * 4),
    onAdd() {},
    render() {
      const t = (performance.now() % duration) / duration;
      const scale = 1 + (maxScale - 1) * t;
      const opacity = 0.85 * (1 - t);
      ctx.clearRect(0, 0, px, px);
      ctx.save();
      ctx.scale(RATIO, RATIO);
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, (ICON_SIZE / 2) * scale, 0, Math.PI * 2);
      ctx.lineWidth = 2;
      ctx.strokeStyle = color;
      ctx.globalAlpha = opacity;
      ctx.stroke();
      ctx.restore();
      this.data = ctx.getImageData(0, 0, px, px).data;
      map.triggerRepaint();
      return true;
    },
  };
}

/** Cobalt map pin with a light center, 30 pt tall (SearchPinAnnotationMapContent). */
export function drawSearchPin(): ImageData {
  const w = 30;
  const h = 38;
  const c = document.createElement('canvas');
  c.width = w * RATIO;
  c.height = h * RATIO;
  const ctx = c.getContext('2d')!;
  ctx.scale(RATIO, RATIO);
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetY = 1;
  ctx.fillStyle = PALETTE.cobalt;
  ctx.beginPath();
  ctx.arc(w / 2, 13, 12, Math.PI * 0.8, Math.PI * 0.2, false);
  ctx.lineTo(w / 2, h - 2);
  ctx.closePath();
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.fillStyle = PALETTE.mist;
  ctx.beginPath();
  ctx.arc(w / 2, 13, 5.5, 0, Math.PI * 2);
  ctx.fill();
  return imageData(c);
}

/** Up or down arrow in a category color for the gauge next-stage label. */
export function drawArrow(direction: 'up' | 'down', color: string): ImageData {
  const size = 14;
  const [c, ctx] = canvas(size);
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  const top = direction === 'up' ? 2 : size - 2;
  const bottom = direction === 'up' ? size - 2 : 2;
  const head = direction === 'up' ? 7 : size - 7;
  ctx.beginPath();
  ctx.moveTo(size / 2, bottom);
  ctx.lineTo(size / 2, top);
  ctx.moveTo(3, head);
  ctx.lineTo(size / 2, top);
  ctx.lineTo(size - 3, head);
  ctx.stroke();
  return imageData(c);
}

export function arrowIconId(direction: 'up' | 'down', category: FloodCategory | 'none'): string {
  return `arrow-${direction}-${category}`;
}

/** Adds every sprite the layers reference. Safe to call once per map load. */
export function registerMapIcons(map: MaplibreMap): void {
  for (const kind of ALERT_KINDS) {
    const id = alertIconId(kind);
    if (!map.hasImage(id)) map.addImage(id, drawAlertIcon(kind), { pixelRatio: RATIO });
    if (ALERT_STYLE[kind].shape === 'triangle') {
      const hid = haloIconId(kind);
      if (!map.hasImage(hid)) map.addImage(hid, pulsingHalo(map, ALERT_STYLE[kind].color), { pixelRatio: RATIO });
    }
  }
  if (!map.hasImage(SEARCH_PIN_ICON)) map.addImage(SEARCH_PIN_ICON, drawSearchPin(), { pixelRatio: RATIO });
  const categories: (FloodCategory | 'none')[] = [...(Object.keys(FLOOD_CATEGORY_INFO) as FloodCategory[]), 'none'];
  for (const cat of categories) {
    const color = cat === 'none' ? '#ffffff' : FLOOD_CATEGORY_INFO[cat].color;
    for (const dir of ['up', 'down'] as const) {
      const id = arrowIconId(dir, cat);
      if (!map.hasImage(id)) map.addImage(id, drawArrow(dir, color), { pixelRatio: RATIO });
    }
  }
}
